import {
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../../features/userSlice";
import { useDispatch } from "react-redux";
import { CalculationTypesEndPointLogic } from "../../../../../logic/DataCollection/Unifier/CalculationTypesEndpointLogic";
import {
  validateDescriptionField,
} from "../../../../../utils/helpers";

interface EditCalculationProps {
  open: boolean;
  handleClose: () => void;
}

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Paper sx={{ padding: "16px", mb: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

// what the PUT body is built from, so "nothing changed" can be detected by
// comparing the form against the row it was seeded with
const emptyForm = {
  name: "",
  description: "",
  atom_phrase: "",
  custom_expression: "",
  deep_view_explanation: "",
  plain_english_text: "",
  is_active: 1,
  deep_view_is_documented: 0,
  has_missed_run_warning: 0,
};

type CalculationTypeForm = typeof emptyForm;

const toForm = (row: any): CalculationTypeForm => ({
  name: row?.name ?? "",
  description: row?.description ?? "",
  atom_phrase: row?.atom_phrase ?? "",
  custom_expression: row?.custom_expression ?? "",
  deep_view_explanation: row?.deep_view_explanation ?? "",
  plain_english_text: row?.plain_english_text ?? "",
  is_active: row?.is_active ? 1 : 0,
  deep_view_is_documented: row?.deep_view_is_documented ? 1 : 0,
  has_missed_run_warning: row?.has_missed_run_warning ? 1 : 0,
});

const EditCalculationType = ({ open, handleClose }: EditCalculationProps) => {
  const { selectedRow } = useSelector(
    (state: any) => state.unifierCalculationTypes,
  );
  const [loading, setLoading] = useState(false);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();

  const [form, setForm] = useState<CalculationTypeForm>(emptyForm);
  const [initialForm, setInitialForm] =
    useState<CalculationTypeForm>(emptyForm);
  const [id, setId] = useState("");
  const [error, setError] = useState({
    name: "",
    description: "",
    atomPhrase: "",
  });

  useEffect(() => {
    if (selectedRow) {
      setForm(toForm(selectedRow));
      setInitialForm(toForm(selectedRow));
      setId(selectedRow.id);
      setError({ name: "", description: "", atomPhrase: "" });
    }
  }, [selectedRow]);

  const setField = (field: keyof CalculationTypeForm, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  //-- Function to close the drawer and reset the form
  const closeEditCollectionDrawer = () => {
    setForm(emptyForm);
    setInitialForm(emptyForm);
    setId("");
    setError({ name: "", description: "", atomPhrase: "" });
    handleClose();
  };

  //-- Function to check if the form is valid
  const isFormValid = () => {
    return (
      form.name?.trim() !== "" &&
      form.description?.trim() !== "" &&
      form.atom_phrase?.trim() !== "" &&
      !error.name &&
      !error.description &&
      !error.atomPhrase
    );
  };

  //-- Only the fields the author actually touched are sent
  const getChangedFields = () => {
    const changed: Partial<CalculationTypeForm> = {};

    (Object.keys(form) as (keyof CalculationTypeForm)[]).forEach((field) => {
      if (form[field] !== initialForm[field]) {
        changed[field] = form[field] as any;
      }
    });

    return changed;
  };

  // an untouched form has nothing to save
  const hasChanges = Object.keys(getChangedFields()).length > 0;

  //-- Function to update calculation type
  const handleUpdateCalculationType = async () => {
    if (!isFormValid()) return;

    const data = getChangedFields();
    // nothing to send when the row came back untouched
    if (Object.keys(data).length === 0) {
      closeEditCollectionDrawer();
      return;
    }

    setLoading(true);
    try {
      const response = await CalculationTypesEndPointLogic.editCollectionTypes(
        oauthTokenHeader,
        data,
        id,
      );

      if (!response || !response.status) {
        throw new Error("Failed to update calculation type.");
      } else if (response.status === 200) {
        await CalculationTypesEndPointLogic.getCollectionTypes(
          oauthTokenHeader,
          dispatch,
        );
        closeEditCollectionDrawer();
        dispatch(
          setDisplayErrorSnackBar({
            message: "Calculation type has been updated successfully.",
            type: "success",
          }),
        );
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "An unexpected error occurred.";

      dispatch(
        setDisplayErrorSnackBar({ message: errorMessage, type: "error" }),
      );

      return; // Stop execution, do not close the drawer
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      sx={{ zIndex: 1300 }}
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        closeEditCollectionDrawer();
      }}
      PaperProps={{ sx: { width: "40%", minWidth: "480px" } }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ padding: "16px" }}
        >
          <Typography variant="h3" sx={{ fontSize: "20px" }}>
            Edit Calculation Type
          </Typography>
          <IconButton
            data-tracker="380"
            onClick={closeEditCollectionDrawer}
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider />

        <Box sx={{ padding: "16px", overflowY: "auto", flex: 1 }}>
          <Section title="Details">
            {/* the name identifies the type elsewhere, so it is read-only
                once the type exists */}
            <TextField
              value={form.name}
              disabled
              label="Calculation Name"
              color="secondary"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={form.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setField("description", e.target.value);
                setError((prev) => ({
                  ...prev,
                  description: validateDescriptionField(e.target.value),
                }));
              }}
              error={!!error.description}
              helperText={error.description}
              required
              label="Description"
              placeholder="What does this type do"
              color="secondary"
              size="small"
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              value={form.atom_phrase}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setField("atom_phrase", e.target.value);
                setError((prev) => ({
                  ...prev,
                  atomPhrase: e.target.value.trim()
                    ? ""
                    : "This field is required.",
                }));
              }}
              error={!!error.atomPhrase}
              helperText={error.atomPhrase}
              required
              label="Atom Phrase — Stage 2 Explanation"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              value={form.custom_expression}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("custom_expression", e.target.value)
              }
              label="Custom Expression"
              color="secondary"
              size="small"
              fullWidth
            />
          </Section>

          <Section title="Deep View Documentation">
            <TextField
              value={form.deep_view_explanation}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("deep_view_explanation", e.target.value)
              }
              label="Deep View Explanation"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              value={form.plain_english_text}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("plain_english_text", e.target.value)
              }
              label="Plain English Text"
              color="secondary"
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Section>

          <Section title="Settings">
            <Stack>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={Boolean(form.is_active)}
                    onChange={(event) =>
                      setField("is_active", event.target.checked ? 1 : 0)
                    }
                  />
                }
                label="Set as active"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={Boolean(form.deep_view_is_documented)}
                    onChange={(event) =>
                      setField(
                        "deep_view_is_documented",
                        event.target.checked ? 1 : 0,
                      )
                    }
                  />
                }
                label="Documented"
              />
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={Boolean(form.has_missed_run_warning)}
                    onChange={(event) =>
                      setField(
                        "has_missed_run_warning",
                        event.target.checked ? 1 : 0,
                      )
                    }
                  />
                }
                label="Missed Run Warning"
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button
                data-tracker="379"
                variant="contained"
                color="secondary"
                loading={loading}
                loadingPosition="end"
                endIcon={loading && <SaveIcon />}
                disabled={!isFormValid() || !hasChanges}
                onClick={handleUpdateCalculationType}
              >
                Save changes <SaveOutlinedIcon sx={{ ml: 1 }} />
              </Button>
            </Stack>
          </Section>
        </Box>
      </Box>
    </Drawer>
  );
};

export default EditCalculationType;
